import { useTranslation } from "react-i18next";
import { useTenant } from "../../hooks/multiTenancy";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client";
import { creditTransactionsQuery, userQuery } from "../../apollo-client/queries";
import { CreditTransaction, FidelityCustomer } from "../../apollo-client/types";
import { getLocalizedTransactionType, getPrepaidAccount, getPrice } from "../../utils";
import { IonBackButton, IonButtons, IonCard, IonCol, IonContent, IonGrid, IonHeader, IonInfiniteScroll, IonInfiniteScrollContent, IonLoading, IonPage, IonRefresher, IonRefresherContent, IonRow, IonTitle, IonToolbar, isPlatform, RefresherEventDetail } from "@ionic/react";
import { IonInfiniteScrollCustomEvent } from "@ionic/core";
import { intlFormat } from "date-fns";
import { useEffect, useState } from "react";

const PAGE_SIZE = 20;

const SalesPointTransactions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  const { idSalesPoint } = useParams<{ idSalesPoint: string }>();
  const userResponse = useQuery(userQuery);
  const user: FidelityCustomer = userResponse.data?.user;
  const fidelitySalesPoint = (tenant.settings?.mobileCommerceSalesPoints ?? []).find((p) => p.id === Number.parseInt(idSalesPoint))!;
  const prepaidAccount = user ? getPrepaidAccount(user, fidelitySalesPoint.id) : undefined;
  const queryVariables = {
    filter: encodeURIComponent(JSON.stringify({ idFidelityPrepaidAccount: [prepaidAccount?.id] })),
    sorts: encodeURIComponent(JSON.stringify({ date: -1 })),
    start: 0,
    limit: PAGE_SIZE,
  };
  const { loading, data, fetchMore, refetch } = useQuery<{ creditTransactions: { records: CreditTransaction[] } }>(creditTransactionsQuery, {
    skip: !prepaidAccount,
    variables: queryVariables
  });
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);

  useEffect(() => {
    if (data) {
      setTransactions(data.creditTransactions.records);
      setDisableInfiniteScroll(data.creditTransactions.records.length < PAGE_SIZE);
    }
  }, [data, PAGE_SIZE, setTransactions, setDisableInfiniteScroll]);

  const nextPage = async (e: IonInfiniteScrollCustomEvent<void>) => {
    const { data: newData } = await fetchMore({
      variables: {
        ...queryVariables,
        start: transactions.length
      },
    });
    setTransactions([...transactions, ...newData.creditTransactions.records]);
    if (newData.creditTransactions.records.length < PAGE_SIZE) {
      setDisableInfiniteScroll(true);
    }
    e.target.complete();
  };

  const refresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  return (
    <IonPage className="mainSelection">
      <IonHeader>
        <IonToolbar color="secondary">
          <IonButtons slot="start">
            <IonBackButton />
          </IonButtons>
          <IonTitle>{t("transactions.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="pageContainer">
        <IonRefresher slot="fixed" onIonRefresh={refresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <IonGrid>
          <IonRow>
            <IonCol size="12">
              <IonCard className="header">
                <div className="section sales-point">
                  <div className="sales-point-name">{fidelitySalesPoint.name}</div>
                  {
                    fidelitySalesPoint.street
                    && fidelitySalesPoint.city
                    && fidelitySalesPoint.district
                    && <div className="sales-point-address">{fidelitySalesPoint.street} {fidelitySalesPoint.city} {fidelitySalesPoint.district}</div>
                  }
                </div>
                <div className="section section-horizontal">
                  <div className="title">{t("myCredit.credit")}</div>
                  {prepaidAccount && (
                    <div className="value credit">{getPrice(prepaidAccount.amount, tenant.settings?.fidelityAccount?.account.currency)}</div>
                  )}
                </div>
                <IonLoading isOpen={loading} showBackdrop={false} cssClass="infinite-scroll-loader" spinner={isPlatform("ios") ? "lines" : "circular"} />
                {
                  data && data.creditTransactions.records.map((c) =>
                    <div key={c.id} className="section section-horizontal">
                      <div className="transaction-date">
                        {intlFormat(c.date * 1000, { year: "2-digit", month: "2-digit", day: "numeric" }, { locale: i18n.language })}
                      </div>
                      <div className="transaction-type">{t(getLocalizedTransactionType(c.fidelityPrepaidTransactionType))}</div>
                      <div className="transaction-amount">{getPrice(c.amount, tenant.settings?.fidelityAccount?.account.currency)}</div>
                    </div>
                  )
                }
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonInfiniteScroll disabled={disableInfiniteScroll} threshold="200px" onIonInfinite={nextPage}>
          <IonInfiniteScrollContent loadingText={t("loading")} />
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default SalesPointTransactions;
